import React from 'react';
import { motion } from 'framer-motion';

const ShopaholicLandingPage = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Shopping Addiction Self-Help: Track & Overcome Compulsive Buying
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Identify Your Shopaholic Triggers</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Guilt Gauge helps you track shopping urges, recognize patterns, and develop healthier habits to overcome compulsive buying.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Shopping Habits
            </motion.button>
          </div>
          <div className="text-8xl">🛍️</div>
        </div>
      </div>
      
      {/* Signs of Shopping Addiction section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">6 Signs You Might Be Dealing With Shopping Addiction</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Financial Strain</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  You're spending beyond your means and potentially accumulating debt from shopping.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">😔</span>
              <div>
                <h3 className="font-bold dark:text-white">Emotional Relief</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping when feeling down, stressed, or anxious to temporarily boost mood.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🙈</span>
              <div>
                <h3 className="font-bold dark:text-white">Hiding Purchases</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Concealing shopping bags, lying about spending, or feeling shame about buying habits.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🤔</span>
              <div>
                <h3 className="font-bold dark:text-white">Preoccupation</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Constantly thinking about shopping, checking sales, or browsing online stores.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📦</span>
              <div>
                <h3 className="font-bold dark:text-white">Unused Items</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Having many unopened or unused purchases with tags still attached.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔄</span>
              <div>
                <h3 className="font-bold dark:text-white">Failed Attempts</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Trying to cut back on shopping multiple times without success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Guilt Gauge Helps Overcome Shopping Addiction</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Your Shopping Emotions
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Identify emotional triggers that lead to compulsive buying and shopping sprees.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Measure Shopping Guilt
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Quantify the feelings of regret, shame, and anxiety that follow compulsive purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Personalized Recovery Strategies
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive tailored advice based on your specific shopping addiction patterns.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Take Shopping Recovery Challenges
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Complete structured activities designed to break the compulsive buying cycle.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Recovery Stories from Former Shopaholics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was spending over $1,000 a month on clothes I never wore. After tracking my habits with Guilt Gauge for 3 months, I've cut my impulse shopping by 70% and finally feel in control."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Melissa T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Recovering Shopaholic</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "The emotional shopping challenges helped me recognize my triggers. Now when I feel stressed, I use my alternative coping strategies instead of browsing online stores."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">David K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">8 Months Shopping Sober</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Frequently Asked Questions About Shopping Addiction</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is compulsive shopping a real addiction?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes, shopping addiction (also called compulsive buying disorder or oniomania) is recognized by mental health professionals as a behavioral addiction with patterns similar to other addictive behaviors.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I know if I'm a shopaholic?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Signs include shopping to feel better emotionally, hiding purchases, feeling guilt after shopping, spending beyond your means, and experiencing withdrawal-like symptoms when not shopping. Track your habits with Guilt Gauge to gain insight.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Can I overcome shopping addiction on my own?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Many people successfully manage compulsive shopping with self-help tools like Guilt Gauge. However, consider also speaking with a mental health professional, especially if your shopping has led to significant financial or relationship problems.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What causes someone to become a shopaholic?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Shopping addiction can be triggered by various factors including depression, anxiety, perfectionism, low self-esteem, difficulty controlling impulses, and even genetic predispositions to addictive behaviors. Tracking with Guilt Gauge helps identify your personal triggers.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Overcome Shopping Addiction?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your purchases and triggers today. It's free, private, and stored only on your device.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Start Your Recovery Journey
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Shopping Addiction</h2>
          <p className="mb-3">
            Shopping addiction, also known as compulsive buying disorder, affects approximately 5-7% of the population. It involves a persistent, excessive, and often impulsive urge to shop, despite negative consequences like financial problems, relationship issues, and emotional distress.
          </p>
          <p className="mb-3">
            Guilt Gauge provides tools to track shopping urges, identify emotional triggers, and develop healthier coping mechanisms. Our personalized approach helps you understand the psychological patterns behind compulsive spending and build sustainable recovery strategies.
          </p>
          <p>
            Whether you're dealing with occasional shopping binges or a more severe addiction, our free app offers self-help strategies that complement professional treatment. All information is stored locally on your device, ensuring your privacy throughout your recovery journey.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ShopaholicLandingPage;
